<template>
	<div class="pp-detail">
		<div v-if="intestazione" class="pp-head">
			<v-avatar size="20px" tile class="mr-1">
				<img :src="setting.logo" :alt="servizio.nome" />
			</v-avatar>
			<span class="pp-head-title">Configurazione {{ servizio.nome }}</span>
			<span class="pp-muted ml-2">{{ modello.active ? 'attivo' : 'disattivato' }}</span>
		</div>

		<div class="pp-body">
			<v-alert v-if="servizio.webhook || servizio.nota" outlined text dense type="info" class="text-body-2 mb-4">
				<template v-if="servizio.webhook">
					Configurare il Webhook dell'app inserendo il seguente url <b class="pp-url">{{ servizio.webhook }}</b>.
				</template>
				<div v-if="servizio.nota" v-html="servizio.nota"></div>
			</v-alert>

			<template v-if="servizio.campi.length || servizio.pem">
				<div class="pp-section">Credenziali</div>
				<div class="pp-grid mb-4">
					<template v-for="c in servizio.campi">
						<div v-if="c.tipo === 'switch'" :key="c.key" class="pp-switch">
							<span>{{ c.label }}</span>
							<v-switch v-model="setting[c.key]" color="primary" class="mt-0 pt-0" hide-details></v-switch>
						</div>
						<v-text-field
							v-else-if="c.tipo === 'password'"
							:key="c.key"
							v-model="setting[c.key]"
							:type="visibili[c.key] ? 'text' : 'password'"
							:append-icon="visibili[c.key] ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
							:label="c.label"
							autocomplete="new-password"
							dense
							outlined
							hide-details
							background-color="white"
							@click:append="$set(visibili, c.key, !visibili[c.key])"
						/>
						<v-text-field v-else :key="c.key" v-model="setting[c.key]" :label="c.label" dense outlined hide-details background-color="white" />
					</template>
					<div v-if="servizio.pem" class="pp-full">
						<v-file-input v-model="filePem" label="File PEM" accept=".pem" show-size dense outlined hide-details background-color="white" @change="$emit('upload-pem', $event)"></v-file-input>
						<a v-if="setting.pem" :href="setting.pem" target="_blank" class="pp-small">File PEM caricato</a>
					</div>
				</div>
			</template>

			<div class="pp-section">Commissione</div>
			<div class="pp-grid mb-4">
				<v-select v-model="setting.typefee" :items="fees" label="Tipo di fee" dense outlined hide-details background-color="white"></v-select>
				<v-text-field
					v-model="setting.fee"
					:suffix="setting.typefee === 'Percentuale' ? '%' : setting.typefee === 'Fisso' ? '€' : ''"
					label="Valore fee"
					type="number"
					dense
					outlined
					hide-details
					background-color="white"
				></v-text-field>
			</div>

			<template v-if="testi.length">
				<div class="pp-section">Testo al checkout</div>
				<div class="pp-texts mb-4">
					<div class="pp-tabs-bar">
						<v-tabs v-model="tab" show-arrows height="38" background-color="transparent" class="pp-tabs">
							<v-tab v-for="t in testi" :key="t.lang" class="px-3" :title="t.text ? '' : 'Testo vuoto'">
								<v-avatar size="16px" tile class="mr-1"><img :src="getImageFlag(t.lang)" :alt="t.lang" /></v-avatar>
								{{ String(t.lang).toUpperCase() }}
								<span v-if="!t.text" class="pp-dot" />
							</v-tab>
						</v-tabs>
						<div v-if="testi.length > 1" class="pp-tabs-actions">
							<v-btn x-small text color="primary" title="Copia il testo della prima lingua in tutte le altre" @click="$emit('copy')">
								<v-icon left x-small>mdi-content-copy</v-icon>Copia su tutte
							</v-btn>
							<v-btn v-if="translate" x-small text color="primary" title="Traduci i testi con Google Translate" @click="$emit('translate')">
								<v-icon left x-small>mdi-translate</v-icon>Traduci
							</v-btn>
						</div>
					</div>
					<div class="pp-tab-body">
						<v-textarea
							v-if="testi[tab]"
							:key="testi[tab].lang"
							v-model="testi[tab].text"
							:label="'Testo ' + String(testi[tab].lang).toUpperCase()"
							rows="3"
							auto-grow
							dense
							outlined
							hide-details
							background-color="white"
						></v-textarea>
					</div>
				</div>
			</template>

			<div class="pp-section">{{ servizio.abilitati ? 'Metodi e opzioni' : 'Opzioni' }}</div>
			<div class="pp-grid">
				<v-select
					v-if="servizio.abilitati"
					v-model="setting.enabled"
					:items="enablepaypal"
					label="Metodi abilitati"
					multiple
					item-value="name"
					item-text="name"
					:menu-props="{ closeOnContentClick: true }"
					dense
					outlined
					hide-details
					background-color="white"
				>
					<template v-slot:item="data">
						<v-list-item @click="$emit('toggle-enabled', data.item)">
							<img :src="getImagePath(data.item.name)" alt="" class="pp-option-logo" />
							<v-list-item-content>
								<v-list-item-title>{{ data.item.name }}</v-list-item-title>
							</v-list-item-content>
						</v-list-item>
					</template>
					<template v-slot:selection="data">
						<v-chip v-if="data.item" small close @click:close="$emit('toggle-enabled', data.item)">
							<img :src="getImagePath(data.item.name)" alt="" class="pp-option-logo" />
							{{ data.item.name }}
						</v-chip>
					</template>
				</v-select>
				<v-select
					v-model="setting.options"
					:items="options"
					label="Opzioni"
					multiple
					return-object
					:menu-props="{ closeOnContentClick: true }"
					dense
					outlined
					hide-details
					background-color="white"
					:class="{ 'pp-full': !servizio.abilitati }"
				>
					<template v-slot:item="data">
						<v-list-item @click="$emit('toggle-option', data.item)">
							<img :src="data.item.logo" alt="" class="pp-option-logo" />
							<v-list-item-content>
								<v-list-item-title>{{ data.item.name }}</v-list-item-title>
							</v-list-item-content>
						</v-list-item>
					</template>
					<template v-slot:selection="data">
						<v-chip v-if="data.item" small close @click:close="$emit('toggle-option', data.item)">
							<img :src="data.item.logo" alt="" class="pp-option-logo" />
							{{ data.item.name }}
						</v-chip>
					</template>
				</v-select>
			</div>
		</div>

		<div class="pp-foot">
			<v-btn v-if="servizio.connessione" small outlined color="primary" @click="$emit('check', servizio.connessione)"><v-icon left small>mdi-connection</v-icon>Verifica connessione</v-btn>
			<v-spacer></v-spacer>
			<v-btn depressed small color="primary" @click="$emit('save')">Conferma</v-btn>
		</div>
	</div>
</template>

<script>
// riquadro di configurazione di un metodo di pagamento: riga espansa (desktop) e card (mobile).
// I campi scrivono direttamente in modello.setting; copia, traduzione, opzioni e upload li fa la pagina.
export default {
	name: 'PaymentPanel',
	props: {
		// { nome, campi: [{ key, label, tipo? }], webhook?, nota?, pem?, abilitati? }
		servizio: { type: Object, required: true },
		modello: { type: Object, required: true },
		fees: { type: Array, default: () => [] },
		options: { type: Array, default: () => [] },
		enablepaypal: { type: Array, default: () => [] },
		// traduzione automatica attiva sul profilo
		translate: { type: Boolean, default: false },
		getImageFlag: { type: Function, required: true },
		getImagePath: { type: Function, default: () => null },
		intestazione: { type: Boolean, default: true },
	},
	data() {
		return {
			tab: 0,
			filePem: null,
			visibili: {},
		};
	},
	computed: {
		setting() {
			return this.modello.setting || {};
		},
		testi() {
			return this.setting.trans || [];
		},
	},
};
</script>

<style scoped>
.pp-detail {
	border: 1px solid rgba(0, 0, 0, 0.12);
	border-radius: 10px;
	overflow: hidden;
	background: #f3f6fa;
	width: 100%;
}
.pp-head {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 4px;
	padding: 8px 12px;
	background: #fff;
	border-bottom: 1px solid #e1e7ef;
	font-size: 13px;
}
.pp-head-title {
	font-weight: 600;
	color: #1f2d3d;
}
.pp-body {
	padding: 12px;
}
.pp-section {
	font-size: 11px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	color: #5f6b7a;
	margin-bottom: 6px;
}
.pp-grid {
	display: grid;
	grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
	gap: 12px 16px;
}
.pp-full {
	grid-column: 1 / -1;
}
.pp-muted {
	color: #5f6b7a;
}
.pp-small {
	font-size: 13px;
}
.pp-url {
	word-break: break-all;
}
.pp-switch {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	background: #fff;
	border: 1px solid #e1e7ef;
	border-radius: 8px;
	padding: 6px 12px;
	font-size: 14px;
}
/* testi per lingua: tab compatte + un solo campo */
.pp-texts {
	background: #fff;
	border: 1px solid #e1e7ef;
	border-radius: 8px;
	overflow: hidden;
}
.pp-tabs-bar {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	border-bottom: 1px solid #eef2f7;
}
.pp-tabs {
	flex: 1 1 0;
	min-width: 0;
	width: auto;
}
.pp-tabs ::v-deep .v-tab {
	min-width: 0;
	font-size: 12px;
	letter-spacing: 0.03em;
}
.pp-tabs-actions {
	display: flex;
	gap: 2px;
	padding: 0 6px;
}
.pp-dot {
	width: 6px;
	height: 6px;
	border-radius: 50%;
	background: #f0a020;
	margin-left: 4px;
}
.pp-tab-body {
	padding: 10px;
}
.pp-option-logo {
	width: 20px;
	height: 20px;
	margin-right: 8px;
	object-fit: contain;
}
.pp-foot {
	display: flex;
	justify-content: flex-end;
	gap: 8px;
	padding: 8px 12px;
	background: #fff;
	border-top: 1px solid #e1e7ef;
}

@media (max-width: 768px) {
	.pp-body {
		padding: 8px;
	}
	.pp-grid {
		grid-template-columns: 1fr;
	}
}
</style>
